import styled, { css } from "styled-components";
import { fontSizeMap, fontWeightMap } from "../Text/Text.style.js";
import { TextWeight } from "../Text/Text.types.js";
import { ITheme } from "../Theme/Theme.types.js";
import { StyleInputProps } from "./Input.types.js";

const getIconSize = (size: StyleInputProps["size"]): number => (size === "large" ? 1.25 : size === "medium" ? 1 : 0.75);

const getBaseHorizontallPaddingRem = (props: StyleInputProps & { theme: ITheme }): number => {
    if (props.variant === "borderless" && props.size === "large") return 0.75;
    return 1;
};

const getInputHorizontalPadding = (hasIcon: boolean, props: StyleInputProps & { theme: ITheme }): number => {
    if (!hasIcon) return getBaseHorizontallPaddingRem(props);
    return getIconSize(props.size) + getBaseHorizontallPaddingRem(props) + 0.75;
};

const getFocusHoverBorderColor = (props: StyleInputProps & { theme: ITheme }): string | false => {
    if (props.variant === "borderless") {
        if (props.value) return props.theme.grayscale[70];
        return props.theme.grayscale[50];
    }

    if (props.disabled) return false;
    if (props.status !== undefined) return props.theme.colors[props.status][100];
    return props.theme.colors.primary[100];
};

const getBorderColor = (props: StyleInputProps & { theme: ITheme }): string => {
    if (props.disabled) return props.theme.grayscale[50];
    if (props.variant === "borderless" && props.status === undefined) return "transparent";
    if (props.status === undefined) return props.variant === "solid" ? props.theme.grayscale[50] : props.theme.grayscale.white;
    return props.theme.colors[props.status][100];
};

const getBorderRadius = (props: StyleInputProps & { theme: ITheme }): string => {
    if (props.size === "small") return props.theme.radius.xs;
    return props.theme.radius.s;
};

const getColor = (props: StyleInputProps & { theme: ITheme }): string => {
    if (props.variant === "borderless") return "transparent";
    return props.disabled ? props.theme.grayscale[50] : props.theme.grayscale[100];
};

const getFontWeight = (props: StyleInputProps & { theme: ITheme }): number | undefined => {
    if (props.variant !== "borderless" || props.fontWeight) return props.fontWeight;

    const blFontWidthMap: Record<StyleInputProps["size"], number> = {
        large: fontWeightMap[TextWeight.BOLD],
        medium: fontWeightMap[TextWeight.NORMAL],
        small: fontWeightMap[TextWeight.NORMAL],
    };

    return blFontWidthMap[props.size];
};

const getFontSizeMap = (props: StyleInputProps & { theme: ITheme }): Record<StyleInputProps["size"], number> => {
    if (props.variant === "borderless")
        return {
            large: fontSizeMap.large,
            medium: fontSizeMap.small,
            small: fontSizeMap["xx-small"],
        };

    return {
        large: fontSizeMap.small,
        medium: fontSizeMap["x-small"],
        small: fontSizeMap["xx-small"],
    };
};

const getVerticalPaddingMap = (props: StyleInputProps & { theme: ITheme }): Record<StyleInputProps["size"], number> => {
    if (props.variant === "borderless")
        return {
            large: 0.5,
            medium: 0.75,
            small: 0.25,
        };

    return {
        large: 0.875,
        medium: 0.75,
        small: 0.5,
    };
};

const BaseSpanAbsolute = styled.span`
    position: absolute;
    line-height: 0;
    top: 50%;
    transform: translateY(-50%);
`;
export const PrependSpanAbsolute = styled(BaseSpanAbsolute)``;
export const AppendSpanAbsolute = styled(BaseSpanAbsolute)``;
export const PrependSpan = styled.span<{ asPrependIcon: boolean }>`
    gap: ${(props) => `${props.theme.spacing[8]}`};
    display: flex;
    align-items: center;
    margin-left: ${(props) => `${props.asPrependIcon ? props.theme.spacing[44] : props.theme.spacing[16]}`};
    min-width: fit-content;
`;
export const AppendSpan = styled.span`
    gap: ${(props) => `${props.theme.spacing[8]}`};
    display: flex;
    align-items: center;
    margin-right: ${(props) => `${props.theme.spacing[16]}`};
`;

export const StyledDivWithBorder = styled.div``;
export const StyledDivFlex = styled.div``;

export const StyledDivBody = styled.div<{ canBreakLine: boolean }>`
    width: 100%;
    position: relative;
    padding: 0 !important;
    margin: 0 ${(props) => (props.canBreakLine ? props.theme.rem(0.5) : props.theme.rem(0))};
    display: flex;
    align-items: center;
`;

export const StyledInput = styled.label<StyleInputProps>`
    display: flex;
    margin: 0;
    flex-direction: column;
    width: ${(props) => (props.width ? props.width : "auto")};
    max-width: ${(props) => (props.maxWidth ? props.maxWidth : "initial")};
    gap: ${(props) => props.theme.spacing[8]};
    ${(props) =>
        props.isStatic &&
        !props.disabled &&
        css`
            cursor: pointer;
        `}

    & > ${StyledDivWithBorder} {
        position: relative;
        padding: 0;
        background-color: ${(props) => (props.variant === "borderless" ? "transparent" : props.theme.grayscale.whiteGlass)};
        border: ${(props) => `2px solid ${getBorderColor(props)}`};
        border-radius: ${(props) => getBorderRadius(props)};
        color: ${(props) => getColor(props)};

        transition: all 0.2s ease;

        & > ${StyledDivFlex} {
            display: flex;
        }

        &:hover:not(:focus-within) {
            border: 2px solid ${(props) => getFocusHoverBorderColor(props)};
        }

        &:focus-within {
            outline: none;
            border: 2px solid ${(props) => getFocusHoverBorderColor(props)};
        }

        & input,
        & textarea {
            resize: none;
            background-color: transparent;
            width: 100%;
            text-align: ${(props) => props.textAlign};
            font-size: ${(props) => props.fontSize ?? props.theme.rem(getFontSizeMap(props)[props.size])};
            font-weight: ${(props) => getFontWeight(props)};
            padding: ${(props) =>
                `${props.theme.rem(getVerticalPaddingMap(props)[props.size])} ${props.theme.rem(
                    getInputHorizontalPadding(props.hasAppendIcon, props),
                )} ${props.theme.rem(getVerticalPaddingMap(props)[props.size])} ${props.theme.rem(getInputHorizontalPadding(props.hasPrependIcon, props))}`};
            border: 0;
            color: ${(props) => (props.disabled ? props.theme.grayscale[50] : props.theme.grayscale[100])};
            ${(props) =>
                props.isStatic &&
                !props.disabled &&
                css`
                    cursor: pointer;
                    caret-color: transparent;
                    text-overflow: ellipsis;
                `}
            box-shadow: none;

            &:hover {
                box-shadow: none;
            }

            &:focus {
                outline: none;
            }

            ::placeholder {
                color: ${(props) => (props.disabled ? props.theme.grayscale[50] : props.theme.grayscale[70])};
            }
        }

        & textarea {
            height: auto;
            min-height: auto;
            max-height: ${(props) => (props.maxRows ? props.theme.rem(getFontSizeMap(props)[props.size] * props.maxRows + 1) : "auto")};
        }

        ${PrependSpanAbsolute}, ${AppendSpanAbsolute} {
            & svg {
                width: ${(props) => props.theme.rem(getIconSize(props.size))};
                height: ${(props) => props.theme.rem(getIconSize(props.size))};
            }

            color: ${(props) => (props.disabled ? props.theme.grayscale[50] : props.theme.grayscale[70])};
        }

        ${PrependSpanAbsolute} {
            left: ${(props) => props.theme.rem(getBaseHorizontallPaddingRem(props))};
        }

        ${AppendSpanAbsolute} {
            right: ${(props) => props.theme.rem(getBaseHorizontallPaddingRem(props))};
        }
    }
`;
